import React from 'react'

// Structured mention token: @[Full Name](user_id). Stored in the message content;
// parsed at send (→ message_mentions rows) and at render (→ @Name chips). Shared so
// the inbox-note mention surface (CP3) can reuse it.
const TOKEN_RE = /@\[([^\]]+)\]\(([0-9a-fA-F-]{36})\)/g

export interface ParsedMention { name: string; userId: string }

// Extract tokens → unique by userId (one mention row per user per message).
export function parseMentions(content: string): ParsedMention[] {
  const out: ParsedMention[] = []
  const seen = new Set<string>()
  const re = new RegExp(TOKEN_RE.source, 'g')
  let m: RegExpExecArray | null
  while ((m = re.exec(content)) !== null) {
    const userId = m[2]
    if (!seen.has(userId)) { seen.add(userId); out.push({ name: m[1], userId }) }
  }
  return out
}

// Render content with tokens turned into @Name chips; everything else stays text.
export function renderWithMentions(content: string): React.ReactNode {
  const nodes: React.ReactNode[] = []
  const re = new RegExp(TOKEN_RE.source, 'g')
  let last = 0
  let i = 0
  let m: RegExpExecArray | null
  while ((m = re.exec(content)) !== null) {
    if (m.index > last) nodes.push(content.slice(last, m.index))
    nodes.push(
      <span key={`m${i++}`} style={{ color: 'var(--accent-text)', background: 'var(--accent-dim)', borderRadius: 4, padding: '0 3px', fontWeight: 600 }}>
        @{m[1]}
      </span>,
    )
    last = m.index + m[0].length
  }
  if (last < content.length) nodes.push(content.slice(last))
  return nodes.length ? nodes : content
}

// Replace the active @query (trailing /@\w*$/ before the caret) with the token + a
// trailing space. Returns the new value + new caret position.
export function insertMentionToken(
  value: string, caret: number, name: string, userId: string,
): { value: string; caret: number } {
  const before = value.slice(0, caret)
  const after = value.slice(caret)
  const at = before.search(/@\w*$/)
  if (at < 0) return { value, caret }
  const token = `@[${name}](${userId}) `
  return { value: before.slice(0, at) + token + after, caret: at + token.length }
}

// Remove every @[Name](userId) token for a specific userId from the content,
// consuming the trailing space the inserter added (so no double-space gap is left).
// Tokens for other users are preserved verbatim. Used by the draft-mention pills.
export function removeMentionToken(content: string, userId: string): string {
  const re = new RegExp(TOKEN_RE.source + ' ?', 'g')
  return content.replace(re, (full, _name, id) => (id === userId ? '' : full))
}
